import {RealMixer} from '../logics/globals';

import {
  INITIAL_MIXER_DISTANCE_SETTING,
  INITIAL_MIXER_FADER_SETTING,
  INITIAL_MIXER_MUTE_SETTING,
  INITIAL_MIXER_SETTING
} from './const';

import type {HEL_AUDIO_TYPE_VIDEO, HEL_AUDIO_TYPE_VOICE, HelAudioTypeIndex} from './const';
import type {VideoPlayInfo} from './video_callee';
import type {JsonObject} from '../../../HeliportJS/dist/models/json';
import type {
  ProcedureCallFunc,
  ProcedureOnclickedAvatarCallFunc,
  ProcedureOnloadedCallFunc,
  ProcedureOnfirsttouchCallFunc,
  ProcedureOnresizeCallFunc,
  ProcedureSkywayCommandCallFunc,
  ProcedureSkywayMyinfoCallFunc
} from '../types/internal';
import type {HlsConfig} from 'hls.js';

declare global {
  /* eslint-disable no-var -- NOTE: TypeScriptのグローバルスコープ拡張では「var」が必須 */
  var g_WEBGL_debug_renderer_info: WEBGL_debug_renderer_info | undefined;

  var g_OES_standard_derivatives: OES_standard_derivatives | undefined;
  var g_OES_texture_float: OES_texture_float | undefined;
  var g_OES_texture_float_linear: OES_texture_float_linear | undefined;
  var g_OES_texture_half_float_linear: OES_texture_half_float_linear | undefined;
  var g_OES_texture_float: OES_texture_float | undefined;
  var g_Use_OES_texture_float_linear: boolean | undefined;
  var g_Use_OES_standard_derivatives: boolean | undefined;

  var g_WEBGL_depth_texture: WEBGL_depth_texture | undefined;
  var g_ANGLE_instanced_arrays: ANGLE_instanced_arrays | undefined;
  var g_EXT_texture_filter_anisotropic: EXT_texture_filter_anisotropic | undefined;
  var g_UnmaskedRendererWebGL: string | undefined;

  var g_hel_MixerReal: unknown | undefined;

  var g_VideoMediaStreamSrcObject: MediaStream | undefined | null;

  var g_SceneJsonPath: string | undefined;
  var g_hel_OnTellScreenStartFunction: (() => void) | undefined;
  var g_hel_OnTellWaitScreenShareFunction: (() => void) | undefined;

  var g_OnfirsttouchProcedureList: ProcedureOnfirsttouchCallFunc[] | undefined;

  var g_AnimationFrameRequest: number | undefined;
  var g_XRSession: XRSession | undefined;

  var g_CanvasLayerShow: {[key: string]: boolean} | undefined;

  // Video Recorder
  var g_VideoRecorder: MediaRecorder | null | undefined;
  var g_RecordedVideoBlobObj: Blob | null | undefined;
  var g_RecordedVideoBlobURL: string | null | undefined;
  var g_VideoRecorderExtension: string | null | undefined;
  var g_VideoRecordCanvas: HTMLCanvasElement | null | undefined;
  var g_VideoRecordContext: CanvasRenderingContext2D | null | undefined;

  // Audio Context
  var g_audioContext: AudioContext | null | undefined;

  // Screenshot
  var g_ScreenshotData: string | null | undefined;
  /* eslint-enable */
}

Object.assign(globalThis, {
  g_CanvasLayerShow: {}
});

/**
 * WorldIdAccessor(GET)
 * @returns String
 */
export const getWorldId = (): string | undefined => {
  return globalThis.g_WorldID;
};

/**
 * WorldIdAccessor(SET)
 * @remarks
 * 未使用
 * worldIdがstringでなく入ってくる可能性も考えられるためunknown型で定義
 * front側で設定することが多い。var設定
 * @param worldId グローバルワールドID
 */
export const setWorldId = (worldId: unknown) => {
  if (typeof worldId !== 'string') {
    return;
  }
  globalThis.g_WorldID = worldId;
};

/**
 * SkyRoomIdAccessor(GET)
 * @returns TODO: 内海 default値がわからないため0にしてる
 */
export const getSkyRoomId = (): string => {
  return globalThis.g_Skyway_RoomID ?? '0';
};

/**
 * SkyRoomIdAccessor(SET)
 * @remarks
 * front側で設定することが多い。var設定
 * @param skyRoomId グローバルスカイルームID
 */
export const setSkyRoomId = (skyRoomId: string) => {
  globalThis.g_Skyway_RoomID = skyRoomId;
};

/**
 * SpotIdAccessor(GET)
 * @returns String
 */
export const getSpotId = (): string | undefined => {
  return globalThis.g_Spot;
};

/**
 * SpotIdAccessor(SET)
 * @remarks
 * 未使用
 * spotIdがstringでなく入ってくる可能性も考えられるためunknown型で定義
 * front側で設定することが多い。var設定
 * @param spotId グローバルスポットID
 */
export const setSpotId = (spotId: unknown) => {
  if (typeof spotId !== 'string') {
    return;
  }
  globalThis.g_Spot = spotId;
};

/**
 * WorldSelectedToggle (GET)
 * @returns Boolean
 */
export const getWorldSelectedToggle = (): boolean => {
  return globalThis.g_ShowWorldSelect ?? false;
};

/**
 * WorldSelectedToggle(SET)
 * @remarks
 * 未使用
 * toggleがboolean以外の可能性があるためunknown型に
 * front側で設定することが多い。var設定
 * @param toggle ワールド選択されてるか
 */
export const setWorldSelectedToggle = (toggle: unknown) => {
  if (typeof toggle !== 'boolean') {
    return;
  }
  globalThis.g_ShowWorldSelect = toggle;
};

/**
 * LoggedInStatusToggle (GET)
 * @returns Boolean
 */
export const getLoggedInStatusToggle = (): boolean => {
  return globalThis.g_Logined ?? false;
};

/**
 * LoggedInStatusToggle(SET)
 * @remarks
 * toggleがbooleanでなく入ってくる可能性も考えられるためunknown型で定義
 *
 * @param toggle ログイン状態
 */
export const setLoggedInStatusToggle = (toggle: unknown) => {
  if (typeof toggle !== 'boolean') {
    return;
  }
  globalThis.g_Logined = toggle;
};

/**
 * CanvasName (GET)
 * @returns String
 */
export const getCanvasFileName = (): string => {
  return globalThis.g_CanvasFileName ?? '';
};

/**
 * CanvasName(SET)
 * @remarks
 * updatedCanvasNameがstringでなく入ってくる可能性も考えられるためunknown型で定義
 *
 * @param updatedCanvasName 更新予定のキャンバスファイル名
 */
export const setCanvasFileName = (updatedCanvasName: unknown) => {
  if (typeof updatedCanvasName !== 'string') {
    return;
  }
  globalThis.g_CanvasFileName = updatedCanvasName;
};

/*
 * パックファイル
 */
export const getPackFileList = (): Array<string> | undefined => {
  return globalThis.g_PackFileList;
};

export const addPackFile = (
  packFileName: string,
  priority: Number,
  condition: Number,
  useCDN: boolean
) => {
  if (!globalThis.g_PackFileList) {
    throw new Error('g_PackFileList is not defined');
  }
  globalThis.g_PackFileList.push(
    packFileName +
      ',' +
      priority.toString() +
      ',' +
      condition.toString() +
      ',' +
      (useCDN ? 'true' : 'false')
  );
};

/**
 * CompImageListFileName (GET)
 * @returns String
 */
export const getCompImageListFileName = (): string => {
  return globalThis.g_CompImageListFileName ?? '';
};

export const getCompImageListUseCDN = (): boolean => {
  return globalThis.g_CompImageListUseCDN ?? false;
};

/**
 * CompImageListFileName(SET)
 * @remarks
 * updatedCompImageListFileNameがstringでなく入ってくる可能性も考えられるためunknown型で定義
 *
 * @param updatedCanvasName 更新予定のキャンバスファイル名
 */
export const setCompImageListFileName = (
  updatedCompImageListFileName: unknown,
  useCDN: boolean
) => {
  if (typeof updatedCompImageListFileName !== 'string') {
    return;
  }
  globalThis.g_CompImageListFileName = updatedCompImageListFileName;
  globalThis.g_CompImageListUseCDN = useCDN;
};

/**
 * CurrentUserName (GET)
 * @returns String
 */
export const getCurrentUserName = (): string => {
  return globalThis.g_CurrentUserName ?? '';
};

/**
 * CurrentUserName(SET)
 * @remarks
 * updatedCanvasNameがstringでなく入ってくる可能性も考えられるためunknown型で定義
 *
 * @param updatedUserName 更新予定のユーザ名
 */
export const setCurrentUserName = (updatedUserName: string) => {
  globalThis.g_CurrentUserName = updatedUserName;
};

/**
 * WebGL10Toggle (GET)
 * @remarks
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns Boolean
 */
export const getWebGL10Toggle = (): boolean => {
  return globalThis.g_UseWebGL10 ?? false;
};

/**
 * WebGL10Toggle(SET)
 * @remarks
 * updatedMixerTabValueがnumberでなく入ってくる可能性も考えられるためunknown型で定義
 *
 * @param toggle フラグ
 */
export const setWebGL10Toggle = (toggle: unknown) => {
  if (typeof toggle !== 'boolean') {
    return;
  }
  globalThis.g_UseWebGL10 = toggle;
};

/**
 * WebGLDebugRendererInfo(GET)
 * @returns WEBGL_debug_renderer_info|undefined
 */
export const getWebGLDebugRendererInfo = (): WEBGL_debug_renderer_info | undefined => {
  return globalThis.g_WEBGL_debug_renderer_info;
};

/**
 * WebGLDebugRendererInfo(SET)
 * @param webglDebugRendererInfo
 */
export const setWebGLDebugRendererInfo = (
  webglDebugRendererInfo: WEBGL_debug_renderer_info | null
) => {
  if (!webglDebugRendererInfo) {
    return;
  }
  globalThis.g_WEBGL_debug_renderer_info = webglDebugRendererInfo;
};

/**
 * WebGLDebugRendererInfoToggle(SET)
 * @param toggle
 */
export const setWebGLDebugRendererInfoToggle = (toggle: boolean) => {
  Object.assign(globalThis, {g_Use_WEBGL_debug_renderer_info: toggle});
};

/**
 * UnmaskedRendererWebGL(SET)
 * @param text
 * @returns
 */
export const setUnmaskedRendererWebGL = (text: string) => {
  Object.assign(globalThis, {g_UnmaskedRendererWebGL: text});
};

/**
 * UnmaskedVendorWebGL(SET)
 * @param text
 * @returns
 */
export const setUnmaskedVendorWebGL = (text: string) => {
  if (typeof text !== 'string') {
    return;
  }
  Object.assign(globalThis, {g_UnmaskedVendorWebGL: text});
};

/**
 * IntelWebGLPlatform(GET)
 * @returns Intel製GPUかどうか
 */
export const isIntelWebGLPlatform = () => {
  if (globalThis.g_UnmaskedRendererWebGL === undefined) {
    return false;
  }

  return globalThis.g_UnmaskedRendererWebGL.toLowerCase().indexOf('intel') >= 0;
};

/**
 * CompressedTexturePvrtc (GET)
 * @remarks
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns WEBGL_compressed_texture_pvrtc|undefined
 */
export const getTextureExtOnPvrtc = (): WEBGL_compressed_texture_pvrtc | undefined => {
  return globalThis.g_extPVRTC ?? undefined;
};

/**
 * CompressedTexturePvrtc(SET)
 * @remarks
 * compressedTexturePvrtc以外が入ってくる可能性も考えられるためunknown型で定義
 *
 * @param compressedTexturePvrtc
 */
export const setTextureExtOnPvrtc = (compressedTexturePvrtc: WEBGL_compressed_texture_pvrtc) => {
  globalThis.g_extPVRTC = compressedTexturePvrtc;
};

/**
 * TextureEtc2Toggle (GET)
 * @remarks
 * 未使用
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns Boolean
 */
export const getTexturePvrtcToggle = (): boolean => {
  if (
    globalThis.g_TextureCompression_PVRTC === undefined ||
    globalThis.g_TextureCompression_PVRTC === null
  ) {
    return false;
  }
  return globalThis.g_TextureCompression_PVRTC;
};

/**
 * TextureEtc2Toggle(SET)
 * @remarks
 * toggleが違う形式で入ってくる可能性も考えられるためunknown型で定義
 *
 * @param toggle
 */
export const setTexturePvrtcToggle = (toggle: unknown) => {
  if (typeof toggle !== 'boolean') {
    return;
  }
  globalThis.g_TextureCompression_PVRTC = toggle;
};

/**
 * CompressedTextureEtc2 (GET)
 * @remarks
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns WEBGL_compressed_texture_etc|undefined
 */
export const getTextureExtOnETC2 = (): WEBGL_compressed_texture_etc | undefined => {
  if (globalThis.g_extETC2 === undefined || globalThis.g_extETC2 === null) {
    return undefined;
  }
  return globalThis.g_extETC2;
};

/**
 * CompressedTextureEtc2(SET)
 * @param compressedTextureEtc2
 */
export const setTextureExtOnETC2 = (compressedTextureEtc2: WEBGL_compressed_texture_etc) => {
  globalThis.g_extETC2 = compressedTextureEtc2;
};

/**
 * TextureEtc2Toggle (GET)
 * @remarks
 * 未使用
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns Boolean
 */
export const getTextureETC2Toggle = (): boolean => {
  if (
    globalThis.g_TextureCompression_ETC2 === undefined ||
    globalThis.g_TextureCompression_ETC2 === null
  ) {
    return false;
  }
  return globalThis.g_TextureCompression_ETC2;
};

/**
 * TextureEtc2Toggle(SET)
 * @remarks
 * toggleが違う形式で入ってくる可能性も考えられるためunknown型で定義
 *
 * @param toggle
 */
export const setTextureETC2Toggle = (toggle: unknown) => {
  if (typeof toggle !== 'boolean') {
    return;
  }
  globalThis.g_TextureCompression_ETC2 = toggle;
};

/**
 * CompressedTextureASTC (GET)
 * @remarks
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns WEBGL_compressed_texture_astc|undefined
 */
export const getTextureExtOnAstc = (): WEBGL_compressed_texture_astc | undefined => {
  if (globalThis.g_extASTC === undefined || globalThis.g_extASTC === null) {
    return undefined;
  }
  return globalThis.g_extASTC;
};

/**
 * CompressedTextureASTC(SET)
 * @param compressedTextureAstc
 */
export const setTextureExtOnAstc = (compressedTextureAstc: WEBGL_compressed_texture_astc) => {
  globalThis.g_extASTC = compressedTextureAstc;
};

/**
 * TextureASTCToggle (GET)
 * @remarks
 * 未使用
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns Boolean
 */
export const getTextureAstcToggle = (): boolean => {
  if (
    globalThis.g_TextureCompression_ASTC === undefined ||
    globalThis.g_TextureCompression_ASTC === null
  ) {
    return false;
  }
  return globalThis.g_TextureCompression_ASTC;
};

/**
 * TextureASTCToggle(SET)
 * @remarks
 * boolean以外が違う形式で入ってくる可能性も考えられるためunknown型で定義
 * @param toggle
 */
export const setTextureAstcToggle = (toggle: unknown) => {
  if (typeof toggle !== 'boolean') {
    return;
  }
  globalThis.g_TextureCompression_ASTC = toggle;
};

/**
 * CompressedTextureDXT (GET)
 * @remarks
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns any|undefined
 */
export const getTextureExtOnDxt = (): any | undefined => {
  if (!globalThis.g_extDXT === undefined || globalThis.g_extDXT === null) {
    return undefined;
  }
  return globalThis.g_extDXT;
};

/**
 * CompressedTextureDXT(SET)
 * @remarks
 * compressedTextureDxt以外が入ってくる可能性も考えられるためunknown型で定義
 *
 * @param compressedTextureDxt
 */
export const setTextureExtOnDxt = (compressedTextureDxt: unknown) => {
  if (compressedTextureDxt === undefined || compressedTextureDxt === null) {
    return;
  }
  globalThis.g_extDXT = compressedTextureDxt;
};

/**
 * TextureDXTToggle (GET)
 * @remarks
 * 未使用
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns Boolean
 */
export const getTextureDxtToggle = (): boolean => {
  if (
    globalThis.g_TextureCompression_DXT === undefined ||
    globalThis.g_TextureCompression_DXT === null
  ) {
    return false;
  }
  return globalThis.g_TextureCompression_DXT;
};

/**
 * TextureDXTToggle(SET)
 * @remarks
 * boolean以外が違う形式で入ってくる可能性も考えられるためunknown型で定義
 * @param toggle
 */
export const setTextureDxtToggle = (toggle: unknown) => {
  if (typeof toggle !== 'boolean') {
    return;
  }
  globalThis.g_TextureCompression_DXT = toggle;
};

/**
 * ExtensionShaderTextureLod (GET)
 * @remarks
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns EXT_shader_texture_lod|undefined
 */
export const getExtShaderTextureLod = (): EXT_shader_texture_lod | undefined => {
  if (
    globalThis.g_EXT_shader_texture_lod === undefined ||
    globalThis.g_EXT_shader_texture_lod === null
  ) {
    return undefined;
  }
  return globalThis.g_EXT_shader_texture_lod;
};

/**
 * ExtensionShaderTextureLod(SET)
 * @remarks
 * extShaderTextureLod以外が入ってくる可能性も考えられるためunknown型で定義
 *
 * @param extShaderTextureLod
 */
export const setExtShaderTextureLod = (extShaderTextureLod: unknown) => {
  if (extShaderTextureLod === undefined || extShaderTextureLod === null) {
    return;
  }
  globalThis.g_EXT_shader_texture_lod = extShaderTextureLod;
};

/**
 * OesStandardDerivatives(GET)
 * @remarks
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns OES_standard_derivatives|undefined
 */
export const getOesStandardDerivatives = (): OES_standard_derivatives | undefined => {
  return globalThis.g_OES_standard_derivatives;
};

/**
 * OesStandardDerivatives(SET)
 * @param oesStandardDerivatives
 */
export const setOesStandardDerivatives = (
  oesStandardDerivatives: OES_standard_derivatives | null
) => {
  if (!oesStandardDerivatives) {
    return;
  }
  Object.assign(globalThis, {g_Use_OES_standard_derivatives: oesStandardDerivatives});
};

/**
 * ExtShaderTextureLodToggle (GET)
 * @remarks
 * 未使用
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns Boolean
 */
export const getExtShaderTextureLodToggle = (): boolean => {
  if (
    globalThis.g_Use_EXT_shader_texture_lod === undefined ||
    globalThis.g_Use_EXT_shader_texture_lod === null
  ) {
    return false;
  }
  return globalThis.g_Use_EXT_shader_texture_lod;
};

/**
 * ExtShaderTextureLodToggle(SET)
 * @remarks
 * boolean以外が違う形式で入ってくる可能性も考えられるためunknown型で定義
 * @param toggle
 */
export const setExtShaderTextureLodToggle = (toggle: unknown) => {
  if (typeof toggle !== 'boolean') {
    return;
  }
  globalThis.g_Use_EXT_shader_texture_lod = toggle;
};

/**
 * OesStandardDerivatives(SET)
 * @param toggle
 */
export const setOesStandardDerivativesToggle = (toggle: boolean) => {
  Object.assign(globalThis, {g_Use_OES_standard_derivatives: toggle});
};

/**
 * ExtColorBufferFloat (GET)
 * @remarks
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns undefined
 */
export const getExtColorBufferFloat = (): EXT_color_buffer_float | undefined => {
  return globalThis.g_EXT_color_buffer_float ?? undefined;
};

/**
 * ExtColorBufferFloat(SET)
 * @remarks
 *
 * @param ExtColorBufferFloat
 */
export const setExtColorBufferFloat = (ExtColorBufferFloat: unknown) => {
  if (ExtColorBufferFloat === undefined || ExtColorBufferFloat === null) {
    return;
  }
  globalThis.g_EXT_color_buffer_float = ExtColorBufferFloat;
};
/**
 * ExtColorBufferHalfFloat (GET)
 * @remarks
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns EXT_color_buffer_half_float|undefined
 */
export const getExtColorBufferHalfFloat = (): EXT_color_buffer_half_float | undefined => {
  if (
    globalThis.g_EXT_color_buffer_half_float === undefined ||
    globalThis.g_EXT_color_buffer_half_float === null
  ) {
    return undefined;
  }
  return globalThis.g_EXT_color_buffer_half_float;
};

/**
 * ExtColorBufferHalfFloat(SET)
 * @param extColorBufferHalfFloat
 */
export const setExtColorBufferHalfFloat = (
  extColorBufferHalfFloat: EXT_color_buffer_half_float
) => {
  globalThis.g_EXT_color_buffer_half_float = extColorBufferHalfFloat;
};

/**
 * ExtColorBufferToggle (GET)
 * @remarks
 * 未使用
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns Boolean
 */
export const getExtColorBufferToggle = (): boolean => {
  if (
    globalThis.g_Use_EXT_color_buffer === undefined ||
    globalThis.g_Use_EXT_color_buffer === null
  ) {
    return false;
  }
  return globalThis.g_Use_EXT_color_buffer;
};

/**
 * ExtColorBufferToggle(SET)
 * @remarks
 * boolean以外が違う形式で入ってくる可能性も考えられるためunknown型で定義
 * @param toggle
 */
export const setExtColorBufferToggle = (toggle: unknown) => {
  if (typeof toggle !== 'boolean') {
    return;
  }
  globalThis.g_Use_EXT_color_buffer = toggle;
};

/**
 * WebglTextureFloat (GET)
 * @remarks
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns WEBGL_depth_texture|undefined
 */
export const getWebglDepthTexture = (): WEBGL_depth_texture | undefined => {
  return globalThis.g_WEBGL_depth_texture;
};

/**
 * OesTextureFloat (GET)
 * @remarks
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns OES_texture_float|undefined
 */
export const getOesTextureFloat = (): OES_texture_float | undefined => {
  return globalThis.g_OES_texture_float;
};

/**
 * OesTextureHalfFloat (GET)
 * @remarks
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns OES_texture_half_float|undefined
 */
export const getOesTextureHalfFloat = (): OES_texture_half_float | undefined => {
  if (
    globalThis.g_OES_texture_half_float === undefined ||
    globalThis.g_OES_texture_half_float === null
  ) {
    return undefined;
  }
  return globalThis.g_OES_texture_half_float;
};

/**
 * OesTextureFloatLinear (GET)
 * @remarks
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns OES_texture_float_linear|undefined
 */
export const getOesTextureFloatLinear = (): OES_texture_float_linear | undefined => {
  return globalThis.g_OES_texture_float_linear;
};

/**
 * OesTextureHalfFloatLinear (GET)
 * @remarks
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns OES_texture_half_float_linear|undefined
 */
export const getOesTextureHalfFloatLinear = (): OES_texture_half_float_linear | undefined => {
  return globalThis.g_OES_texture_half_float_linear;
};

/**
 * WebglDepthTexture(SET)
 * @param oesTextureFloat
 */
export const setWebglDepthTexture = (webglDepthTexture: WEBGL_depth_texture | null) => {
  if (!webglDepthTexture) {
    return;
  }
  Object.assign(globalThis, {g_WEBGL_depth_texture: webglDepthTexture});
};

/**
 * OesTextureFloat(SET)
 * @param oesTextureFloat
 */
export const setOesTextureFloat = (oesTextureFloat: OES_texture_float | null) => {
  if (!oesTextureFloat) {
    return;
  }
  Object.assign(globalThis, {g_OES_texture_float: oesTextureFloat});
};

/**
 * OesTextureHalfFloat(SET)
 * @param oesTextureHalfFloat
 */
export const setOesTextureHalfFloat = (oesTextureHalfFloat: OES_texture_half_float) => {
  globalThis.g_OES_texture_half_float = oesTextureHalfFloat;
};

/**
 * OesTextureFloatLinear(SET)
 * @param oesTextureFloatLinear
 */
export const setOesTextureFloatLinear = (
  oesTextureFloatLinear: OES_texture_float_linear | null
) => {
  if (!oesTextureFloatLinear) {
    return;
  }
  Object.assign(globalThis, {g_OES_texture_float_linear: oesTextureFloatLinear});
};

/**
 * OesTextureHalfFloatLinear(SET)
 * @remarks
 * boolean以外が違う形式で入ってくる可能性も考えられるためunknown型で定義
 * @param oesTextureHalfFloatLinear
 */
export const setOesTextureHalfFloatLinear = (
  oesTextureHalfFloatLinear: OES_texture_half_float_linear | null
) => {
  if (!oesTextureHalfFloatLinear) {
    return;
  }
  Object.assign(globalThis, {g_OES_texture_half_float_linear: oesTextureHalfFloatLinear});
};

/**
 * ExtTextureFilterAnisotropic (GET)
 * @remarks
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns
 */
export const getExtTextureFilterAnisotropic = (): EXT_texture_filter_anisotropic | undefined => {
  return globalThis.g_EXT_texture_filter_anisotropic;
};

/**
 * ExtTextureFilterAnisotropic(SET)
 * @param extTextureFilterAnisotropic
 */
export const setExtTextureFilterAnisotropic = (
  extTextureFilterAnisotropic: EXT_texture_filter_anisotropic | null
) => {
  if (!extTextureFilterAnisotropic) {
    return;
  }
  Object.assign(globalThis, {g_EXT_texture_filter_anisotropic: extTextureFilterAnisotropic});
};

/**
 * AngleInstancedArrays (GET)
 * @remarks
 * render_calleeのみで利用してる...他と一緒に状態管理してもよいかも
 * @returns ANGLE_instanced_arrays |undefined
 */
export const getAngleInstancedArrays = (): ANGLE_instanced_arrays | undefined => {
  return globalThis.g_ANGLE_instanced_arrays;
};

/**
 * AngleInstancedArrays (SET)
 * @param angleInstancedArray
 */
export const setAngleInstancedArrays = (angleInstancedArray: ANGLE_instanced_arrays | null) => {
  if (!angleInstancedArray) {
    return;
  }
  Object.assign(globalThis, {g_ANGLE_instanced_arrays: angleInstancedArray});
};

/**
 * OesTextureFloatToggle(SET)
 * @param toggle
 */
export const setOesTextureFloatToggle = (toggle: boolean) => {
  Object.assign(globalThis, {g_Use_OES_texture_float: toggle});
};

/**
 * WebglDepthTextureToggle(SET)
 * @param toggle
 */
export const setWebglDepthTextureToggle = (toggle: boolean) => {
  Object.assign(globalThis, {g_Use_WEBGL_depth_texture: toggle});
};

/**
 * ExtTextureFilterAnisotropic (SET)
 * @param toggle
 */
export const setExtTextureFilterAnisotropicToggle = (toggle: boolean) => {
  Object.assign(globalThis, {g_Use_Ext_texture_filter_anisotropic: toggle});
};

/**
 * AngleInstancedArraysToggle (SET)
 * @param toggle
 */
export const setAngleInstancedArraysToggle = (toggle: boolean) => {
  Object.assign(globalThis, {g_Use_ANGLE_instanced_arrays: toggle});
};

/**
 * OesTextureHalfFloatToggle(SET)
 * @param toggle
 */
export const setOesTextureHalfFloatToggle = (toggle: boolean) => {
  Object.assign(globalThis, {g_Use_OES_texture_half_float: toggle});
};

/**
 * OesTextureFloatLinearToggle(SET)
 * @param toggle
 */
export const setOesTextureFloatLinearToggle = (toggle: boolean) => {
  Object.assign(globalThis, {g_Use_OES_texture_float_linear: toggle});
};

/**
 * OesTextureHalfFloatLinearToggle(SET)
 * @param toggle
 */
export const setOesTextureHalfFloatLinearToggle = (toggle: boolean) => {
  Object.assign(globalThis, {g_Use_OES_texture_half_float_linear: toggle});
};

/**
 * MixerTab (GET)
 * @remarks
 * 状態管理であるためシングルトンのように持ちたいかも
 * @returns Number(decimal)
 */
export const getMixerTab = (): number => {
  if (globalThis.g_hel_MixerTabActive === undefined || globalThis.g_hel_MixerTabActive === null) {
    return 1.0;
  }
  return globalThis.g_hel_MixerTabActive;
};

/**
 * MixerTab(SET)
 * @remarks
 * updatedMixerTabValueがnumberでなく入ってくる可能性も考えられるためunknown型で定義
 *
 * @param updatedMixerTabValue 更新予定のMixerタブ値
 */
export const setMixerTab = (updatedMixerTabValue: unknown) => {
  if (typeof updatedMixerTabValue !== 'number') {
    return;
  }
  globalThis.g_hel_MixerTabActive = updatedMixerTabValue;
};

/**
 * Mixer (GET)
 * @param index
 * @returns Number
 */
export const getMixerSettingAt = (index: HelAudioTypeIndex): number => {
  if (globalThis.g_hel_Mixer === undefined || globalThis.g_hel_Mixer === null) {
    return INITIAL_MIXER_SETTING[index];
  }
  return globalThis.g_hel_Mixer[index];
};

/**
 * Mixer(SET)
 * @param volume ボリューム。decimal
 * @param typeIndex 指定インデックス。0より
 */
export const setMixerSettingAt = (volume: number, typeIndex: number) => {
  if (!globalThis.g_hel_Mixer) {
    throw new Error('g_hel_Mixer is not defined');
  }
  globalThis.g_hel_Mixer[typeIndex] = volume;
};

/**
 * MixerReal (SET)
 * @param volume ボリューム。decimal
 * @param typeIndex 指定インデックス。0より
 */
export const setMixerRealSettingAt = (volume: number, typeIndex: number) => {
  RealMixer[typeIndex] = volume;
};

/**
 * MixerFader (GET)
 * @param index
 * @returns Number
 */
export const getMixerFaderSettingAt = (index: HelAudioTypeIndex): number => {
  if (globalThis.g_hel_MixerFade === undefined || globalThis.g_hel_MixerFade === null) {
    return INITIAL_MIXER_FADER_SETTING[index];
  }
  return globalThis.g_hel_MixerFade[index];
};

/**
 * MixerFader(SET)
 * @param volume ボリューム。decimal
 * @param typeIndex 指定インデックス。0より
 */
export const setMixerFaderSettingAt = (volume: number, typeIndex: number) => {
  if (!globalThis.g_hel_MixerFade) {
    throw new Error('g_hel_MixerFade is not defined');
  }
  globalThis.g_hel_MixerFade[typeIndex] = volume;
};

/**
 * MixerDistance (GET)
 * @param index
 * @returns Number
 */
export const getMixerDistanceAt = (index: HelAudioTypeIndex): number => {
  if (globalThis.g_hel_MixerDistance === undefined || globalThis.g_hel_MixerDistance === null) {
    return INITIAL_MIXER_DISTANCE_SETTING[index];
  }
  return globalThis.g_hel_MixerDistance[index];
};

/**
 * MixerDistance(SET)
 * @param volume ボリューム。decimal
 * @param typeIndex 指定インデックス。0より
 */
export const setMixerDistanceAt = (volume: number, typeIndex: number) => {
  if (!globalThis.g_hel_MixerDistance) {
    throw new Error('g_hel_MixerDistance is not defined');
  }
  globalThis.g_hel_MixerDistance[typeIndex] = volume;
};

/**
 * MixerMute (GET)
 * @param index
 * @returns Number
 */
export const getMixerMuteAt = (
  index: typeof HEL_AUDIO_TYPE_VOICE | typeof HEL_AUDIO_TYPE_VIDEO
): number => {
  if (globalThis.g_hel_MixerMute === undefined || globalThis.g_hel_MixerMute === null) {
    return INITIAL_MIXER_MUTE_SETTING[index];
  }
  return globalThis.g_hel_MixerMute[index];
};

/**
 * MixerMute(SET)
 * @param volume ボリューム。decimal
 * @param typeIndex 指定インデックス。0より
 */
export const setMixerMuteAt = (volume: number, typeIndex: number) => {
  if (!globalThis.g_hel_MixerMute) {
    throw new Error('g_hel_MixerMute is not defined');
  }
  globalThis.g_hel_MixerMute[typeIndex] = volume;
};

/**
 * MixerMasterVolume (GET)
 */
export const getMixerMasterVolume = () => {
  if (
    globalThis.g_hel_MixerMasterVolume === undefined ||
    globalThis.g_hel_MixerMasterVolume === null
  ) {
    return 1.0;
  }
  return globalThis.g_hel_MixerMasterVolume;
};

/**
 * MixerMasterVolume(SET)
 * @param volume ボリューム
 */
export const setMixerMasterVolume = (volume: number) => {
  globalThis.g_hel_MixerMasterVolume = volume;
};

/**
 * MixerMasterMute (GET)
 */
export const getMixerMasterMute = () => {
  if (globalThis.g_hel_MixerMasterMute === undefined || globalThis.g_hel_MixerMasterMute === null) {
    return 0;
  }
  return globalThis.g_hel_MixerMasterMute;
};

/**
 * MixerMasterMute(SET)
 * @param volume ボリューム
 */
export const setMixerMasterMute = (mute: number) => {
  globalThis.g_hel_MixerMasterMute = mute;
};

/**
 * UseLegacyAudioToggle (GET)
 * @returns Boolean
 */
export const getUseLegacyAudioToggle = (): boolean => {
  if (globalThis.g_hel_UseLegacyAudio === undefined || globalThis.g_hel_UseLegacyAudio === null) {
    return false;
  }
  return globalThis.g_hel_UseLegacyAudio;
};

/**
 * UseLegacyAudioToggle(SET)
 * @remarks
 * 未使用
 * boolean以外が違う形式で入ってくる可能性も考えられるためunknown型で定義
 * @param toggle
 */
export const setUseLegacyAudioToggle = (toggle: unknown) => {
  if (typeof toggle !== 'boolean') {
    return;
  }
  globalThis.g_hel_UseLegacyAudio = toggle;
};

/**
 * AudioListに処理の流れ上Nullを設定
 */
export const pushNullToAudioList = () => {
  if (!globalThis.g_audioList) {
    globalThis.g_audioList = [];
  }
  globalThis.g_audioList.push(null);
};

/**
 * getVideoCanPlayToggle (GET)
 * @remarks
 * 未使用
 * @returns Boolean
 */
export const getVideoCanPlayToggle = (): boolean => {
  if (globalThis.g_videocanplay === undefined || globalThis.g_videocanplay === null) {
    return false;
  }
  return globalThis.g_videocanplay;
};

/**
 * VideoCanPlayToggle(SET)
 * boolean以外が違う形式で入ってくる可能性も考えられるためunknown型で定義
 * @param toggle
 */
export const setVideoCanPlayToggle = (toggle: unknown) => {
  if (typeof toggle !== 'boolean') {
    return;
  }
  globalThis.g_videocanplay = toggle;
};

/**
 * VideoEndedToggle (GET)
 * @returns Boolean
 */
export const getVideoEndedToggle = (): boolean => {
  if (globalThis.g_videoended === undefined || globalThis.g_videoended === null) {
    return false;
  }
  return globalThis.g_videoended;
};

/**
 * VideoEndedToggle(SET)
 * boolean以外が違う形式で入ってくる可能性も考えられるためunknown型で定義
 * @param toggle
 */
export const setVideoEndedToggle = (toggle: unknown) => {
  if (typeof toggle !== 'boolean') {
    return;
  }
  globalThis.g_videoended = toggle;
};

/**
 * VideoAudioContext (GET)
 * @returns AudioContext|undefined
 */
export const getVideoAudioContext = (): AudioContext | undefined => {
  if (globalThis.g_VideoAudioContext === undefined || globalThis.g_VideoAudioContext === null) {
    return undefined;
  }
  return globalThis.g_VideoAudioContext;
};

/**
 * VideoAudioContext (SET)
 * @param videoAudioContext AudioContext型
 */
export const setVideoAudioContext = (videoAudioContext: AudioContext) => {
  if (videoAudioContext === undefined || videoAudioContext === null) {
    return;
  }
  globalThis.g_VideoAudioContext = videoAudioContext;
};

/**
 * VideoSource (GET)
 * @returns MediaElementAudioSourceNode|undefined
 */
export const getVideoSource = (): MediaElementAudioSourceNode | undefined => {
  if (globalThis.g_VideoSource === undefined || globalThis.g_VideoSource === null) {
    return undefined;
  }
  return globalThis.g_VideoSource;
};

/**
 * VideoSource (SET)
 * @param videoSource MediaElementAudioSourceNode
 */
export const setVideoSource = (videoSource: MediaElementAudioSourceNode) => {
  if (videoSource === undefined || videoSource === null) {
    return;
  }
  globalThis.g_VideoSource = videoSource;
};

/**
 * VideoGainNode (GET)
 * @returns GainNode|undefined
 */
export const getVideoGainNode = (): GainNode | undefined => {
  if (globalThis.g_VideoGainNode === undefined || globalThis.g_VideoGainNode === null) {
    return undefined;
  }
  return globalThis.g_VideoGainNode;
};

/**
 * VideoGainNode (SET)
 * @param gainNode GainNode
 */
export const setVideoGainNode = (gainNode: GainNode) => {
  if (gainNode === undefined || gainNode === null) {
    return;
  }
  globalThis.g_VideoGainNode = gainNode;
};

/**
 * VideoHlsConfig (GET)
 * @returns HlsConfig|undefined
 */
export const getVideoHlsConfig = (): HlsConfig | undefined => {
  if (globalThis.g_VideoHlsConfig === undefined || globalThis.g_VideoHlsConfig === null) {
    return undefined;
  }
  return globalThis.g_VideoHlsConfig;
};

/**
 * VideoHlsConfig (SET)
 * @param updatedConfig HlsConfig
 */
export const setVideoHlsConfig = (updatedConfig: HlsConfig) => {
  if (updatedConfig === undefined || updatedConfig === null) {
    return;
  }
  globalThis.g_VideoHlsConfig = updatedConfig;
};

/**
 * TextureCanvasList (GET)
 * @returns
 */
export const getTextureCanvasList = (): (HTMLCanvasElement | null)[] => {
  if (globalThis.g_TextCanvasList === undefined || globalThis.g_TextCanvasList === null) {
    return [];
  }
  return globalThis.g_TextCanvasList;
};

/**
 * TextureCanvasList(SET)
 * @remarks
 * 必要あればnullを設定
 * @param textureCanvas HTMLCanvasElementインスタンス
 * @param index 設定可能なインデックス
 */
export const setTextureCanvasList = (textureCanvas: HTMLCanvasElement | null, index: number) => {
  if (globalThis.g_TextCanvasList === undefined || globalThis.g_TextCanvasList === null) {
    return;
  }
  globalThis.g_TextCanvasList[index] = textureCanvas;
};

/**
 * ATextureCanvasListに処理の流れ上Nullを設定
 */
export const pushNullToTextureCanvasList = () => {
  if (globalThis.g_TextCanvasList === undefined || globalThis.g_TextCanvasList === null) {
    return;
  }
  globalThis.g_TextCanvasList.push(null);
};

/**
 * GyroActiveToggle (GET)
 * @returns Boolean
 */
export const getGyroActiveToggle = (): boolean => {
  if (globalThis.g_GyroActive === undefined || globalThis.g_GyroActive === null) {
    return false;
  }
  return globalThis.g_GyroActive;
};

/**
 * GyroActiveToggle(SET)
 * boolean以外が違う形式で入ってくる可能性も考えられるためunknown型で定義
 * @param toggle
 */
export const setGyroActiveToggle = (toggle: unknown) => {
  if (typeof toggle !== 'boolean') {
    return;
  }
  globalThis.g_GyroActive = toggle;
};

/**
 * UploadedFileBuffer (GET)
 * @remarks
 * 未使用
 * @returns string|ArrayBuffer|undefined
 */
export const getUploadedFileBuffer = (): string | ArrayBuffer | undefined => {
  if (globalThis.g_hel_UploadFileImage === undefined || globalThis.g_hel_UploadFileImage === null) {
    return undefined;
  }
  return globalThis.g_hel_UploadFileImage;
};

/**
 * UploadedFileBuffer (SET)
 * @param buffer String型 | ArrayBuffer型 | Null
 */
export const setUploadedFileBuffer = (buffer: string | ArrayBuffer | null) => {
  globalThis.g_hel_UploadFileImage = buffer;
};

/**
 * VCUID (GET)
 * @returns String|undefined
 */
export const getVCUID = (): string | undefined => {
  if (
    globalThis.g_VCUID === undefined ||
    globalThis.g_VCUID === null ||
    globalThis.g_VCUID.length === 0
  ) {
    return undefined;
  }
  return globalThis.g_VCUID;
};

/**
 * VCUID (SET)
 * @param vcuid unknown
 */
export const setVCUID = (vcuid: unknown) => {
  if (typeof vcuid !== 'string') {
    return;
  }
  globalThis.g_VCUID = vcuid;
};

/**
 * SkywayName (GET)
 * @returns String
 */
export const getSkywayName = (): string => {
  if (
    globalThis.g_Skyway_MyName === undefined ||
    globalThis.g_Skyway_MyName === null ||
    globalThis.g_Skyway_MyName.length === 0
  ) {
    return 'Guest';
  }
  return globalThis.g_Skyway_MyName;
};

/**
 * SkywayName (SET)
 * @param name unknown
 */
export const setSkywayName = (name: unknown) => {
  if (typeof name !== 'string') {
    return;
  }
  globalThis.g_Skyway_MyName = name;
};

/**
 * SkywayKey (GET)
 * @returns String
 */
export const getSkywayKey = (): string => {
  if (
    globalThis.g_SKYWAY_KEY === undefined ||
    globalThis.g_SKYWAY_KEY === null ||
    globalThis.g_SKYWAY_KEY.length === 0
  ) {
    return '37825849-7771-49bd-890e-61971fb4f431';
  }
  return globalThis.g_SKYWAY_KEY;
};

/**
 * SkywayKey (SET)
 * @remarks
 * 未使用
 * @param key unknown
 */
export const setSkywayKey = (key: unknown) => {
  if (typeof key !== 'string') {
    return;
  }
  globalThis.g_SKYWAY_KEY = key;
};

/**
 * SkywayAvatarNum(GET)
 * @remarks
 * 未使用
 * @returns Number
 */
export const getSkywayAvatarNumber = (): number => {
  if (globalThis.g_Skyway_MyAvatar === undefined || globalThis.g_Skyway_MyAvatar === null) {
    return 0;
  }
  return globalThis.g_Skyway_MyAvatar;
};

/**
 * SkywayAvatarNum(SET)
 * @param avatarNum アバター番号
 */
export const setSkywayAvatarNumber = (avatarNum: number) => {
  globalThis.g_Skyway_MyAvatar = avatarNum;
};

/**
 * SkywayAvatarNum(GET)
 * @returns Number
 */
export const getSkywayAvatarNumberForSend = (): number => {
  if (
    globalThis.g_Skyway_MyAvatar_ForSend === undefined ||
    globalThis.g_Skyway_MyAvatar_ForSend === null
  ) {
    return 0;
  }
  return globalThis.g_Skyway_MyAvatar_ForSend;
};

/**
 * SkywayAvatarNum(SET)
 * @param avatarNum アバター番号
 */
export const setSkywayAvatarNumberForSend = (avatarNum: number) => {
  globalThis.g_Skyway_MyAvatar_ForSend = avatarNum;
};

/**
 * SkywayAvatarIcon(GET)
 * @param
 * @param
 * @returns number
 */

export const getSkywayUseCustomAvatarIcon = () => {
  if (
    globalThis.g_Skyway_Use_CustomAvatarIcon === undefined ||
    globalThis.g_Skyway_Use_CustomAvatarIcon === null
  ) {
    return false;
  }

  return globalThis.g_Skyway_Use_CustomAvatarIcon;
};

/**
 * SkywayAvatarIcon(SET)
 * @param number
 * @param
 * @returns
 */

export const setSkywayUseCustomAvatarIcon = (UseAvatarIcon: boolean) => {
  globalThis.g_Skyway_Use_CustomAvatarIcon = UseAvatarIcon;
};

/**
 * SkywayCustomAvatarIconURL(GET)
 */

export const getSkywayCustomAvatarIconURL = () => {
  if (
    globalThis.g_Skyway_CustomAvatarIcon_URL === undefined ||
    globalThis.g_Skyway_CustomAvatarIcon_URL === null
  ) {
    return '';
  }

  return globalThis.g_Skyway_CustomAvatarIcon_URL;
};

/**
 * SkywayCustomAvatarIconURL(SET)
 */

export const setSkywayCustomAvatarIconURL = (AvatarIconURL: string) => {
  globalThis.g_Skyway_CustomAvatarIcon_URL = AvatarIconURL;
};

/**
 * SkywayStartToggle (GET)
 * @returns Boolean
 */
export const getSkywayStartToggle = (): boolean => {
  if (globalThis.g_Skyway_Start === undefined || globalThis.g_Skyway_Start === null) {
    return false;
  }
  return globalThis.g_Skyway_Start;
};

/**
 * SkywayStartToggle(SET)
 * boolean以外が違う形式で入ってくる可能性も考えられるためunknown型で定義
 * @remarks
 * 未使用
 * @param toggle
 */
export const setSkywayStartToggle = (toggle: unknown) => {
  if (typeof toggle !== 'boolean') {
    return;
  }
  globalThis.g_Skyway_Start = toggle;
};

/**
 * SkywayMicStateTimer (GET)
 * @returns NodeJS.Timer|undefined
 */
export const getSkywayMicStateTimer = (): number | undefined => {
  if (
    globalThis.g_skyway_set_mic_state_timer === undefined ||
    globalThis.g_skyway_set_mic_state_timer === null
  ) {
    return undefined;
  }
  return globalThis.g_skyway_set_mic_state_timer;
};

let liveMicTimer: number | null = null;
/**
 * 発言者モードユーザ用の音量監視タイマーをセットする
 */
export const setLiveMicStateTimer = (timer: number): void => {
  liveMicTimer = timer;
};

/**
 * 発言者モードユーザ用の音量監視タイマーを返す
 * @returns setIntervalのIDナンバーを返す
 */
export const getLiveMicStateTimer = (): number | null => {
  return liveMicTimer;
};

/**
 * SkywayMicStateTimer (SET)
 * @param timer NodeJS.Timer
 */
export const setSkywayMicStateTimer = (timer: number) => {
  if (timer === undefined || timer === null) {
    return;
  }
  globalThis.g_skyway_set_mic_state_timer = timer;
};

/**
 * XrwFriendCode (GET)
 * @remarks
 * XRWorldIngameにて定義あり（helidor_front.js)
 * heliodor.jsやCPP側でも参照あるため定義は必要
 *
 * @returns String
 */
export const getXrwFriendCode = (): string => {
  if (
    globalThis.g_xrwFriendCode === undefined ||
    globalThis.g_xrwFriendCode === null ||
    globalThis.g_xrwFriendCode.length === 0
  ) {
    return '';
  }
  return globalThis.g_xrwFriendCode;
};

/**
 * XrwFriendCode (SET)
 * @remarks
 * 未使用
 * XRWorldIngameにて定義あり（helidor_front.js)
 * heliodor.jsやCPP側でも参照あるため定義は必要
 *
 * @param key unknown
 */
export const setXrwFriendCode = (key: unknown) => {
  if (typeof key !== 'string') {
    return;
  }
  globalThis.g_xrwFriendCode = key;
};

/**
 * XrwDataFolderName (GET)
 * @remarks
 * XRWorldIngameにて定義あり（helidor_front.js)
 *
 * @returns String
 */
export const getXrwDataFolderName = (): string => {
  if (
    globalThis.g_DataFolderName === undefined ||
    globalThis.g_DataFolderName === null ||
    globalThis.g_DataFolderName.length === 0
  ) {
    return '';
  }
  return globalThis.g_DataFolderName;
};

/**
 * XrwDataFolderName (SET)
 * @remarks
 * 未使用
 * XRWorldIngameにて定義あり（helidor_front.js)
 *
 * @param key unknown
 */
export const setXrwDataFolderName = (key: unknown) => {
  if (typeof key !== 'string') {
    return;
  }

  setDataFolderName(key);
};

/**
 * setDataFolderName (SET)
 * @remarks
 *
 * @param DataFolderName string
 */
export const setDataFolderName = (DataFolderName: string) => {
  globalThis.g_DataFolderName = DataFolderName;
};

/**
 * UseSkywayAudioGainToggle (GET)
 * @remarks
 * 未使用
 * XRWorldIngameにて定義あり（helidor_skyway_xrw.js)
 * @returns Boolean
 */
export const getUseSkywayAudioGainToggle = (): boolean => {
  if (
    globalThis.g_hel_UseSkywayAudioGain === undefined ||
    globalThis.g_hel_UseSkywayAudioGain === null
  ) {
    return false;
  }
  return globalThis.g_hel_UseSkywayAudioGain;
};

/**
 * UseSkywayAudioGainToggle(SET)
 * boolean以外が違う形式で入ってくる可能性も考えられるためunknown型で定義
 * @remarks
 * 未使用
 * XRWorldIngameにて定義あり（helidor_skyway_xrw.js)
 * @param toggle
 */
export const setUseSkywayAudioGainToggle = (toggle: unknown) => {
  if (typeof toggle !== 'boolean') {
    return;
  }
  globalThis.g_hel_UseSkywayAudioGain = toggle;
};

/**
 * SceneJson (SET)
 * @remarks
 * @param json
 */
export const setSceneJson = (json: JsonObject) => {
  if (typeof json !== 'string') {
    return;
  }
  Object.assign(globalThis, {g_SceneJson: json});
};

/**
 * SceneJsonPath (GET)
 * @remarks
 * @returns string
 */
export const getSceneJsonPath = (): string | undefined => {
  return globalThis.g_SceneJsonPath;
};

/**
 * SceneJsonPath (SET)
 * @remarks
 * @param path
 */
export const setSceneJsonPath = (path: string) => {
  Object.assign(globalThis, {g_SceneJsonPath: path});
};

/**
 * VideoInfo (SET)
 * @remarks
 * @param json
 */
export const setVideoInfo = (json: JsonObject) => {
  Object.assign(globalThis, {g_VideoInfo: json});
};

/**
 * VideoSourceUrl (SET)
 * @remarks
 * @param url Streaming Video Url
 */
export const setVideoSourceUrl = (url: string) => {
  Object.assign(globalThis, {g_VideoSourceUrl: url});
};

/**
 * setVideoMediastreamSrc (SET)
 * @remarks
 * @param mediaStream Video MediaStream
 */
export const setVideoMediastreamSrc = (mediaStream: MediaStream) => {
  Object.assign(globalThis, {g_VideoMediaStreamSrcObject: mediaStream});
};

/**
 * getVideoMediastreamSrc (GET)
 * @remarks
 * @returns g_Videoで再生するMediaStreamオブジェクト
 */
export const getVideoMediastreamSrc = (): MediaStream | undefined | null => {
  return globalThis.g_VideoMediaStreamSrcObject;
};

/**
 * SkywayOnTellScreenShareStart (GET)
 * @remarks
 * @returns 画面共有の開始事前通知を受け取ったときに呼ばれる関数
 */
export const getSkywayOnTellScreenShareStart = (): (() => void) | undefined => {
  return globalThis.g_hel_OnTellScreenStartFunction;
};

/**
 * SkywayOnTellWaitScreenShare (GET)
 * @remarks
 * @returns 画面共有の待受開始通知を受け取ったときに呼ばれる関数
 */
export const getSkywayOnTellWaitScreenShare = (): (() => void) | undefined => {
  return globalThis.g_hel_OnTellWaitScreenShareFunction;
};

/**
 * SkywayOnScreenShareStarted (SET)
 * @remarks
 * @param version SDKバージョン
 */
export const setHeliodorSdkVersion = (version: string) => {
  if (typeof version !== 'string') {
    return;
  }
  Object.assign(globalThis, {g_HeliodorSDKVersion: version});
};

/**
 * ActionProcedureList (GET)
 * @remarks
 * ブリッジ関数の設定
 */
export const getActionProcedureList = (): (ProcedureCallFunc | undefined)[] => {
  return globalThis.g_ActionProcedureList ?? [];
};

/**
 * ActionProcedureList (SET)
 * @param callFunc
 */
export const setActionProcedureList = (callFunc: ProcedureCallFunc) => {
  if (typeof callFunc !== 'function') {
    return;
  }
  if (!globalThis.g_ActionProcedureList) {
    throw new Error('g_ActionProcedureList is not defined');
  }
  globalThis.g_ActionProcedureList.push(callFunc);
};

/**
 * ActionProcedureList (-)
 */
export const cleanActionProcedureList = () => {
  if (!globalThis.g_ActionProcedureList) {
    throw new Error('g_ActionProcedureList is not defined');
  }
  for (let i = 0; i < globalThis.g_ActionProcedureList.length; i += 1) {
    delete globalThis.g_ActionProcedureList[i];
    globalThis.g_ActionProcedureList[i] = undefined;
  }
  globalThis.g_ActionProcedureList = [];
};

/**
 * OnclickedAvatarProcedureList (GET)
 */
export const getOnclickedAvatarProcedureList = (): ProcedureOnclickedAvatarCallFunc[] => {
  if (
    globalThis.g_OnclickedAvatarProcedureList === undefined ||
    globalThis.g_OnclickedAvatarProcedureList === null
  ) {
    return [];
  }
  return globalThis.g_OnclickedAvatarProcedureList;
};

/**
 * OnclickedAvatarProcedureList (SET)
 * @param callFunc
 */
export const setOnclickedAvatarProcedureList = (callFunc: ProcedureOnclickedAvatarCallFunc) => {
  if (typeof callFunc !== 'function') {
    return;
  }
  if (!globalThis.g_OnclickedAvatarProcedureList) {
    throw new Error('g_OnclickedAvatarProcedureList is not defined');
  }
  globalThis.g_OnclickedAvatarProcedureList.push(callFunc);
};

/**
 * OnloadedProcedureList (GET)
 * @remarks
 * Onloaded関数の設定
 */
export const getOnloadedProcedureList = (): ProcedureOnloadedCallFunc[] => {
  if (
    globalThis.g_OnloadedProcedureList === undefined ||
    globalThis.g_OnloadedProcedureList === null
  ) {
    return [];
  }
  return globalThis.g_OnloadedProcedureList;
};

/**
 * OnloadedProcedureList (SET)
 * @param callFunc
 */
export const setOnloadedProcedureList = (callFunc: ProcedureOnloadedCallFunc) => {
  if (typeof callFunc !== 'function') {
    return;
  }
  if (!globalThis.g_OnloadedProcedureList) {
    throw new Error('g_OnloadedProcedureList is not defined');
  }
  globalThis.g_OnloadedProcedureList.push(callFunc);
};

/**
 * OnfirsttouchProcedureList (GET)
 * @remarks
 * Onfirsttouch関数の設定
 */
export const getOnfirsttouchProcedureList = (): ProcedureOnfirsttouchCallFunc[] => {
  return globalThis.g_OnfirsttouchProcedureList ?? [];
};

/**
 * OnfirsttouchProcedureList (SET)
 * @param callFunc
 */
export const setOnfirsttouchProcedureList = (callFunc: ProcedureOnfirsttouchCallFunc) => {
  if (!globalThis.g_OnfirsttouchProcedureList) {
    throw new Error('g_OnfirsttouchProcedureList is not defined');
  }
  globalThis.g_OnfirsttouchProcedureList.push(callFunc);
};

/**
 * OnresizeProcedureList (GET)
 * @remarks
 * Onloaded関数の設定
 */
export const getOnresizeProcedureList = (): ProcedureOnresizeCallFunc[] => {
  if (
    globalThis.g_OnresizeProcedureList === undefined ||
    globalThis.g_OnresizeProcedureList === null
  ) {
    return [];
  }
  return globalThis.g_OnresizeProcedureList;
};

/**
 * OnresizeProcedureList (SET)
 * @param callFunc
 */
export const setOnresizeProcedureList = (callFunc: ProcedureOnresizeCallFunc) => {
  if (typeof callFunc !== 'function') {
    return;
  }
  if (!globalThis.g_OnresizeProcedureList) {
    throw new Error('g_OnresizeProcedureList is not defined');
  }
  globalThis.g_OnresizeProcedureList.push(callFunc);
};

/**
 * SkywayCommandProcedureList (GET)
 * @remarks
 * Onloaded関数の設定
 */
export const getSkywayCommandProcedureList = (): ProcedureSkywayCommandCallFunc[] => {
  if (
    globalThis.g_SkywayCommandProcedureList === undefined ||
    globalThis.g_SkywayCommandProcedureList === null
  ) {
    return [];
  }
  return globalThis.g_SkywayCommandProcedureList;
};

/**
 * SkywayCommandProcedureList (SET)
 * @param callFunc
 */
export const setSkywayCommandProcedureList = (callFunc: ProcedureSkywayCommandCallFunc) => {
  if (typeof callFunc !== 'function') {
    return;
  }
  if (!globalThis.g_SkywayCommandProcedureList) {
    throw new Error('g_SkywayCommandProcedureList is not defined');
  }
  globalThis.g_SkywayCommandProcedureList.push(callFunc);
};
/**
 * SkywayMyinfoProcedureList (GET)
 * @remarks
 * Onloaded関数の設定
 */
export const getSkywayMyinfoProcedureList = (): ProcedureSkywayMyinfoCallFunc[] => {
  if (
    globalThis.g_SkywayMyinfoProcedureList === undefined ||
    globalThis.g_SkywayMyinfoProcedureList === null
  ) {
    return [];
  }
  return globalThis.g_SkywayMyinfoProcedureList;
};

/**
 * SkywayMyinfoProcedureList (SET)
 * @param callFunc
 */
export const setSkywayMyinfoProcedureList = (callFunc: ProcedureSkywayMyinfoCallFunc) => {
  if (typeof callFunc !== 'function') {
    return;
  }
  if (!globalThis.g_SkywayMyinfoProcedureList) {
    throw new Error('g_SkywayMyinfoProcedureList is not defined');
  }
  globalThis.g_SkywayMyinfoProcedureList.push(callFunc);
};

/**
 * AnimationFrameRequest(SET)
 * @param callFunc
 */
export const setAnimationFrameRequest = (id: number) => {
  Object.assign(globalThis, {g_AnimationFrameRequest: id});
};

/**
 * AnimationFrameRequest(GET)
 * @remarks
 *
 */
export const getAnimationFrameRequest = (): number => {
  return globalThis.g_AnimationFrameRequest ?? -1;
};

/**
 * XRSession(SET)
 * @param callFunc
 */
export const setXRSession = (xrsession: XRSession) => {
  Object.assign(globalThis, {g_XRSession: xrsession});
};

/**
 * XRSession(GET)
 * @remarks
 *
 */
export const getXRSession = (): XRSession | null => {
  return globalThis.g_XRSession ?? null;
};

/**
 * CanvasLayerShow(SET)
 * @param callFunc
 */
export const setCanvasLayerShow = (LayerName: string, show: boolean) => {
  if (!globalThis.g_CanvasLayerShow) {
    throw new Error('g_CanvasLayerShow is not defined');
  }
  globalThis.g_CanvasLayerShow[LayerName] = show;
};

/**
 * CanvasLayerShow(GET)
 * @remarks
 *
 */
export const getCanvasLayerShow = (LayerName: string) => {
  if (!globalThis.g_CanvasLayerShow) {
    throw new Error('g_CanvasLayerShow is not defined');
  }
  return globalThis.g_CanvasLayerShow[LayerName];
};

// Video Recorder
/**
 * g_VideoRecorder(GET)
 */
export const getVideoRecorder = () => {
  return globalThis.g_VideoRecorder;
};

/**
 * g_VideoRecorder(SET)
 */
export const setVideoRecorder = (Recorder: MediaRecorder | null) => {
  globalThis.g_VideoRecorder = Recorder;
};

/**
 * g_RecordedVideoBlobObj(GET)
 */
export const getRecordedVideoBlobObj = () => {
  return globalThis.g_RecordedVideoBlobObj;
};

/**
 * g_RecordedVideoBlobObj(SET)
 */
export const setRecordedVideoBlobObj = (BlobObj: Blob | null) => {
  globalThis.g_RecordedVideoBlobObj = BlobObj;
};

/**
 * g_RecordedVideoBlobURL(GET)
 */
export const getRecordedVideoBlobURL = () => {
  return globalThis.g_RecordedVideoBlobURL ?? null;
};

/**
 * g_RecordedVideoBlobURL(SET)
 */
export const setRecordedVideoBlobURL = (BlobURL: string | null) => {
  globalThis.g_RecordedVideoBlobURL = BlobURL;
};

/**
 * g_VideoRecorderExtension(SET)
 */
export const setVideoRecorderExtension = (Extension: string | null) => {
  globalThis.g_VideoRecorderExtension = Extension;
};

/**
 * g_VideoRecordCanvas(GET)
 */
export const getVideoRecordCanvas = (): HTMLCanvasElement | null => {
  return globalThis.g_VideoRecordCanvas ?? null;
};

/**
 * g_VideoRecordCanvas(SET)
 */
export const setVideoRecordCanvas = (Canvas: HTMLCanvasElement | null) => {
  globalThis.g_VideoRecordCanvas = Canvas;
};

/**
 * g_VideoRecordContext(GET)
 */
export const getVideoRecordContext = (): CanvasRenderingContext2D | null => {
  return globalThis.g_VideoRecordContext ?? null;
};

/**
 * g_VideoRecordContext(SET)
 */
export const setVideoRecordContext = (Context: CanvasRenderingContext2D | null) => {
  globalThis.g_VideoRecordContext = Context;
};

/**
 * 1タブ1AudioContext用
 * g_audioContext(GET)
 */
export const getMainAudioContext = () => {
  return globalThis.g_audioContext ?? null;
};

/**
 * 1タブ1AudioContext用
 * g_audioContext(SET)
 */
export const setMainAudioContext = (Context: AudioContext | null) => {
  globalThis.g_audioContext = Context;
};

/**
 * CanvasIsPortrait(SET)
 * @param callFunc
 */
export const setCanvasIsPortrait = (IsPortrait: boolean) => {
  globalThis.g_CanvasIsPortrait = IsPortrait;
};

/**
 * CanvasIsPortrai(GET)
 * @remarks
 *
 */
export const getCanvasIsPortrait = () => {
  return globalThis.g_CanvasIsPortrait;
};

const canvasGUIPosition: {
  [layoutName: string]:
    | {
        [guiName: string]:
          | {
              [itemName: string]:
                | {
                    x: number;
                    y: number;
                  }
                | undefined;
            }
          | undefined;
      }
    | undefined;
} = {};

Object.assign(globalThis, {
  /** @remarks ブラウザコンソールからのデバッグのために、グローバル変数へ保存する。 */
  g_CanvasGUIPosition: canvasGUIPosition
});

/**
 * CanvasGUIPosition(SET)
 * @param callFunc
 */
export const setCanvasGUIPosition = (params: {
  layoutName: string;
  guiName: string;
  itemName: string;
  x: number;
  y: number;
}) => {
  let layout = canvasGUIPosition[params.layoutName];
  if (!layout) {
    layout = {};
    canvasGUIPosition[params.layoutName] = layout;
  }
  let gui = layout[params.guiName];
  if (!gui) {
    gui = {};
    layout[params.guiName] = gui;
  }
  gui[params.itemName] = {x: params.x, y: params.y};
};

/**
 * CanvasGUIPosition(GET)
 * @remarks
 *
 */
export const getCanvasGUIPosition = (params: {
  layoutName: string;
  guiName: string;
  itemName: string;
}): {x: number; y: number} | undefined => {
  const {layoutName, guiName, itemName} = params;
  return canvasGUIPosition[layoutName]?.[guiName]?.[itemName];
};

const canvasGUIShow: {
  [layoutName: string]:
    | {
        [guiName: string]:
          | {
              [itemName: string]:
                | {
                    show: boolean;
                  }
                | undefined;
            }
          | undefined;
      }
    | undefined;
} = {};

Object.assign(globalThis, {
  /** @remarks ブラウザコンソールからのデバッグのために、グローバル変数へ保存する。 */
  g_CanvasGUIShow: canvasGUIShow
});

/**
 * CanvasGUIPosition(SET)
 * @param callFunc
 */
export const setCanvasGUIShow = (params: {
  layoutName: string;
  guiName: string;
  itemName: string;
  show: boolean;
}) => {
  let layout = canvasGUIShow[params.layoutName];
  if (!layout) {
    layout = {};
    canvasGUIShow[params.layoutName] = layout;
  }
  let gui = layout[params.guiName];
  if (!gui) {
    gui = {};
    layout[params.guiName] = gui;
  }
  gui[params.itemName] = {show: params.show};
};

/**
 * CanvasGUIPosition(GET)
 * @remarks
 *
 */
export const getCanvasGUIShow = (params: {
  layoutName: string;
  guiName: string;
  itemName: string;
}): {show: boolean} | undefined => {
  const {layoutName, guiName, itemName} = params;
  return canvasGUIShow[layoutName]?.[guiName]?.[itemName];
};

/**
 * ScreenshotData(SET)
 * @param data - mainCanvas.toDataURL('image/jpeg') で生成されたBase64エンコードのJPEG画像データ。
 */
export const setScreenshotData = (data: string) => {
  globalThis.g_ScreenshotData = data;
};

/**
 * ScreenshotData(GET)
 * @returns 格納されたBase64エンコードのJPEG画像データ、または worldViewerWindow が未定義の場合は null。
 */
export const getScreenshotData = (): string => {
  return globalThis.g_ScreenshotData || '';
};

let isVideoInitialized = false;
export const getVideoInitialized = () => isVideoInitialized;
export const setVideoInitialized = (initialized: boolean) => {
  isVideoInitialized = initialized;
};

let pendingAutoPlayRequest: VideoPlayInfo | null = null;

export const getPendingAutoPlayRequest = () => pendingAutoPlayRequest;
export const setPendingAutoPlayRequest = (request: VideoPlayInfo | null) => {
  pendingAutoPlayRequest = request;
};

// 画面タップによる初期化をしたことがあるか
let initInteractedFlg = false;
export const getInitInteractedFlg = (): boolean => {
  return initInteractedFlg;
};
export const setInitInteractedFlg = (flg: boolean) => {
  initInteractedFlg = flg;
};

// マイクのアクセス許可をしたことがあるか
let micAccessableFlg = false;
export const getMicAccessableFlg = (): boolean => {
  return micAccessableFlg;
};
export const setMicAccessableFlg = (flg: boolean) => {
  micAccessableFlg = flg;
};
